import { ref } from 'vue';
import { post } from '@/api';
import { createMicrophoneMuteState } from './geminiLiveMicrophoneState';
import { createGeminiLivePlaybackQueue } from './geminiLivePlaybackQueue';
import { createScreenShareState } from './geminiLiveScreenShareState';
import { createGeminiLiveAudioInput } from './geminiLiveAudioInput';

const CONVERSATIONS_URL = 'api/sorty/conversations/';
const PLAYBACK_SAMPLE_RATE = 24000;
const MAX_RECONNECTS = 3;
const PRE_CUTOFF_WARNING_SECONDS = 60;

/**
 * Drives a live (voice / screen-share) session against the dedicated ASGI
 * realtime proxy. The browser speaks our simple proxy protocol; the proxy owns
 * the Vertex Live connection and credentials.
 *
 * NOTE: the live audio/video round-trip requires the deployed proxy + a real
 * Vertex project and cannot be exercised in CI - validate against the live
 * environment before production.
 */
export function useGeminiLive() {
  let micStream = null;

  const status = ref('idle'); // idle|connecting|live|reconnecting|ended|error
  const errorMessage = ref('');
  const modelSpeaking = ref(false);
  const cutoffWarning = ref(false);
  const {
    isScreenSharing,
    markScreenSharingActive,
    markScreenSharingInactive,
  } = createScreenShareState();
  const { isMuted, setMuted, toggleMute } = createMicrophoneMuteState(
    () => micStream
  );
  // Live transcript turns streamed from the proxy ({ role, content, turn_id }).
  // For ephemeral (question-scoped) sessions this is the only place turns live.
  const transcripts = ref([]);
  // The server-side LiveSession this client last minted. Kept after the
  // session ends so a report filed afterwards can still point at it.
  const sessionUuid = ref(null);

  let ws = null;
  let audioCtx = null;
  let micInput = null;
  let screenStream = null;
  let screenVideo = null;
  let screenTimer = null;
  const playbackQueue = createGeminiLivePlaybackQueue(() => audioCtx);
  let cutoffTimer = null;
  let warningTimer = null;
  let reconnects = 0;
  let stopped = false;
  let session = null; // { uuid, wsUrl, token, bootstrap, kind }
  let conversationUuid = null;
  let tokenUrl = null;
  let ephemeral = false;

  /**
   * Start a live session.
   *
   * @param {string} uuid - context uuid (conversation, or question for AskSorty)
   * @param {string} [tokenUrl] - override the token-mint endpoint. Defaults to
   *   the SortyAI conversation endpoint; AskSorty voice passes the per-question
   *   `.../live/token/` route.
   * @param {boolean} [ephemeral] - question-scoped sessions are not persisted:
   *   transcripts stream to the UI but are not written, and there is no
   *   per-session end/transcript endpoint (the proxy ends them on WS close).
   */
  async function start({
    uuid,
    kind = 'VOICE',
    withScreen = false,
    tokenUrl: tokenUrlArg = null,
    ephemeral: ephemeralArg = false,
  }) {
    conversationUuid = uuid;
    ephemeral = ephemeralArg;
    tokenUrl = tokenUrlArg || `${CONVERSATIONS_URL}${uuid}/live/token/`;
    stopped = false;
    reconnects = 0;
    errorMessage.value = '';
    cutoffWarning.value = false;
    setMuted(false);
    markScreenSharingInactive();
    transcripts.value = [];
    status.value = 'connecting';

    const resp = await post(tokenUrl, { kind });
    if (!resp || resp.status >= 400) {
      errorMessage.value =
        resp?.data?.detail ||
        'Could not start the live session. Please try again.';
      status.value = 'error';
      return;
    }
    sessionUuid.value = resp.data.session_uuid ?? null;
    session = {
      uuid: resp.data.session_uuid,
      wsUrl: resp.data.ws_url,
      token: resp.data.token,
      bootstrap: resp.data.bootstrap,
      kind,
    };
    if (!session.wsUrl) {
      errorMessage.value = 'Live sessions are not available right now.';
      status.value = 'error';
      return;
    }
    let mediaRequest = 'microphone';
    try {
      await _openMicrophone();
      if (withScreen) {
        mediaRequest = 'screen';
        await _openScreen();
      }
      _connect();
      _armCutoffTimers();
    } catch {
      errorMessage.value =
        mediaRequest === 'screen'
          ? 'No screen was shared. Choose a tab, window, or screen when your browser asks.'
          : 'Could not access your microphone.';
      await stop();
      status.value = 'error';
    }
  }

  function _connect() {
    ws = new WebSocket(session.wsUrl);
    ws.binaryType = 'arraybuffer';

    ws.onopen = () => {
      reconnects = 0;
      status.value = 'live';
      ws.send(
        JSON.stringify({
          type: 'start',
          token: session.token,
          conversation_uuid: conversationUuid,
          session_uuid: session.uuid,
          kind: session.kind,
        })
      );
    };
    ws.onmessage = (event) => _onMessage(event);
    ws.onerror = () => {};
    ws.onclose = () => _onClose();
  }

  function _onMessage(event) {
    let msg;
    try {
      msg = JSON.parse(event.data);
    } catch {
      return;
    }
    if (msg.type === 'audio' && msg.data) {
      _playAudioChunk(msg.data);
      modelSpeaking.value = true;
    } else if (msg.type === 'interrupted') {
      playbackQueue.interrupt();
      modelSpeaking.value = false;
    } else if (msg.type === 'turn_complete') {
      modelSpeaking.value = false;
    } else if (msg.type === 'transcript') {
      _recordTranscript(msg);
    } else if (msg.type === 'error') {
      errorMessage.value = msg.detail || 'The live session hit an error.';
    }
  }

  // Surface the turn to the UI, then persist it (skipped for ephemeral
  // question-scoped sessions). Dedupe by turn_id so a reconnect can't double it.
  function _recordTranscript(msg) {
    if (!msg.turn_id || !msg.content) return;
    if (!transcripts.value.some((t) => t.turn_id === msg.turn_id)) {
      transcripts.value.push({
        role: msg.role === 'model' ? 'model' : 'user',
        content: msg.content,
        turn_id: msg.turn_id,
      });
    }
    if (ephemeral) return;
    post(
      `${CONVERSATIONS_URL}${conversationUuid}/live/${session.uuid}/transcript/`,
      {
        role: msg.role === 'model' ? 'MODEL' : 'USER',
        content: msg.content,
        client_turn_id: msg.turn_id,
      }
    );
  }

  function _onClose() {
    if (stopped) {
      status.value = 'ended';
      return;
    }
    // Transient drop: reconnect within the session. Session tokens are
    // single-use, so we must mint a FRESH one rather than replay the old token
    // (replaying it would always fail as "invalid or expired token").
    if (reconnects < MAX_RECONNECTS) {
      reconnects += 1;
      status.value = 'reconnecting';
      setTimeout(() => {
        if (!stopped) _remintAndConnect();
      }, 500 * reconnects);
    } else {
      errorMessage.value = 'Lost connection to the live session.';
      status.value = 'error';
      _teardownMedia();
    }
  }

  async function _remintAndConnect() {
    const resp = await post(tokenUrl, { kind: session.kind });
    if (!resp || resp.status >= 400) {
      errorMessage.value = resp?.data?.detail || 'Could not reconnect.';
      status.value = 'error';
      _teardownMedia();
      return;
    }
    // Resumes the same in-progress session server-side; refresh token + url.
    session.uuid = resp.data.session_uuid;
    sessionUuid.value = resp.data.session_uuid ?? sessionUuid.value;
    session.token = resp.data.token;
    if (resp.data.ws_url) session.wsUrl = resp.data.ws_url;
    _connect();
  }

  async function _openMicrophone() {
    // Use the browser's native rate, then resample explicitly. Some browsers
    // ignore a requested AudioContext rate; labelling those samples as 16 kHz
    // makes speech too slow for Gemini's voice activity detector to recognise.
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    micStream = await navigator.mediaDevices.getUserMedia({
      // Echo cancellation is essential: without it the mic re-captures sorty's
      // own spoken output and feeds it back to the model, which then responds to
      // itself in a loop (it never waits for the student).
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    // Permission prompts and token minting cross async boundaries, so Safari
    // and Chromium may leave the context suspended even though capture was
    // granted. Resume it explicitly before wiring the processing graph.
    if (audioCtx.state === 'suspended') await audioCtx.resume();
    micInput = await createGeminiLiveAudioInput({
      audioContext: audioCtx,
      stream: micStream,
      isMuted: () => isMuted.value,
      onPcm16: (pcm16) => {
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        ws.send(
          JSON.stringify({
            type: 'audio',
            data: _arrayBufferToBase64(pcm16.buffer),
          })
        );
      },
    });
  }

  async function _openScreen() {
    screenStream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: false,
    });
    const track = screenStream.getVideoTracks()[0];
    // A muted video element works across Chromium, Firefox, and Safari. The
    // ImageCapture API previously used here is absent in Safari and Firefox.
    screenVideo = document.createElement('video');
    screenVideo.muted = true;
    screenVideo.playsInline = true;
    screenVideo.srcObject = screenStream;
    await screenVideo.play();
    markScreenSharingActive();
    // ~1 fps keeps token cost low; text/diagrams don't need motion.
    screenTimer = setInterval(async () => {
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      try {
        if (!screenVideo.videoWidth || !screenVideo.videoHeight) return;
        const canvas = document.createElement('canvas');
        canvas.width = screenVideo.videoWidth;
        canvas.height = screenVideo.videoHeight;
        canvas.getContext('2d').drawImage(screenVideo, 0, 0);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
        ws.send(JSON.stringify({ type: 'video', data: dataUrl.split(',')[1] }));
      } catch {
        /* a dropped frame is non-fatal */
      }
    }, 1000);
    // If the student stops sharing via the browser UI, end the session.
    track.addEventListener('ended', () => {
      markScreenSharingInactive();
      stop();
    });
  }

  function _playAudioChunk(base64) {
    if (!audioCtx) return;
    const bytes = _base64ToArrayBuffer(base64);
    const pcm16 = new Int16Array(bytes);
    const float = new Float32Array(pcm16.length);
    for (let i = 0; i < pcm16.length; i += 1) float[i] = pcm16[i] / 0x8000;
    const buffer = audioCtx.createBuffer(1, float.length, PLAYBACK_SAMPLE_RATE);
    buffer.getChannelData(0).set(float);
    playbackQueue.enqueue(buffer);
  }

  function _armCutoffTimers() {
    const maxSeconds = session?.bootstrap?.max_session_seconds || 600;
    warningTimer = setTimeout(
      () => {
        cutoffWarning.value = true;
      },
      Math.max(0, (maxSeconds - PRE_CUTOFF_WARNING_SECONDS) * 1000)
    );
    cutoffTimer = setTimeout(() => stop(), maxSeconds * 1000);
  }

  function _teardownMedia() {
    playbackQueue.interrupt();
    micInput?.stop();
    if (micStream) micStream.getTracks().forEach((t) => t.stop());
    if (screenStream) screenStream.getTracks().forEach((t) => t.stop());
    if (screenVideo) {
      screenVideo.pause();
      screenVideo.srcObject = null;
    }
    if (screenTimer) clearInterval(screenTimer);
    markScreenSharingInactive();
    if (audioCtx && audioCtx.state !== 'closed') audioCtx.close();
    micInput = micStream = screenStream = screenVideo = screenTimer = null;
    audioCtx = null;
    setMuted(false);
  }

  async function stop() {
    stopped = true;
    if (warningTimer) clearTimeout(warningTimer);
    if (cutoffTimer) clearTimeout(cutoffTimer);
    try {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'stop' }));
        ws.close();
      }
    } catch {
      /* ignore */
    }
    _teardownMedia();
    // Ephemeral question sessions have no end endpoint; the proxy ends them when
    // the WebSocket closes. Only the conversation flow posts an explicit end.
    if (!ephemeral && session?.uuid && conversationUuid) {
      await post(
        `${CONVERSATIONS_URL}${conversationUuid}/live/${session.uuid}/end/`,
        {}
      );
    }
    status.value = 'ended';
  }

  function _arrayBufferToBase64(buffer) {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i += 1) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  function _base64ToArrayBuffer(base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return bytes.buffer;
  }

  return {
    status,
    errorMessage,
    modelSpeaking,
    cutoffWarning,
    isMuted,
    isScreenSharing,
    transcripts,
    sessionUuid,
    start,
    stop,
    toggleMute,
  };
}
